// @spec GHG-STR-007
// Emission factor card component for search results

import { Check, Loader2, MapPin, Zap } from 'lucide-react';

import { toDisplayActivityUnit } from '../lib/unit-conversion';
import type { EmissionFactor } from '../types';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
interface EmissionFactorCardProps {
  factor: EmissionFactor;
  onClick?: () => void;
  className?: string;
  isSelected?: boolean;
  isPending?: boolean;
  disabled?: boolean;
}

/**
 * Card component displaying an emission factor summary.
 */
export function EmissionFactorCard({
  factor,
  onClick,
  className,
  isSelected,
  isPending,
  disabled,
}: EmissionFactorCardProps) {
  return (
    <Card
      className={cn(
        'relative cursor-pointer transition-colors hover:border-primary/50',
        isSelected && 'border-green-500 bg-green-50/50 dark:bg-green-950/20',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}
      onClick={disabled ? undefined : onClick}
    >
      {isPending && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/60">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}
      {isSelected && !isPending && (
        <div className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-white">
          <Check className="h-4 w-4" />
        </div>
      )}
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="line-clamp-2 text-base">{factor.name}</CardTitle>
          <Badge variant="outline" className="shrink-0">
            {factor.source}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* CO2e Value */}
          <div className="flex items-center gap-2 text-lg font-semibold text-primary">
            <Zap className="h-4 w-4" />
            <span>
              {(() => {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion, @typescript-eslint/no-unnecessary-condition -- API may return string/null at runtime
                const val = Number(factor.co2ePerUnit ?? 0);
                if (val === 0) return '0';
                if (Math.abs(val) < 0.0001) return val.toExponential(3);
                return val.toFixed(4);
              })()}
            </span>
            <span className="text-sm font-normal text-muted-foreground">
              kg CO₂e/{toDisplayActivityUnit(factor.unit)}
            </span>
          </div>

          {/* Metadata */}
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              <span>{factor.regionName}</span>
            </div>
            <span>•</span>
            <span>{factor.year}</span>
            <span>•</span>
            <span>{factor.calculationMethod.toUpperCase()}</span>
          </div>

          {/* Category badges */}
          <div className="flex flex-wrap gap-1">
            <Badge variant="secondary" className="text-xs">
              {factor.category}
            </Badge>
            <Badge variant="secondary" className="text-xs">
              {factor.sector}
            </Badge>
          </div>

          {/* Data quality flags */}
          {factor.dataQualityFlags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {factor.dataQualityFlags.map((flag) => (
                <Badge key={flag} variant="outline" className="text-[10px]">
                  {flag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
